import { useState, useEffect, useCallback, useRef } from 'react'
import { FootballMatch, FootballApiResponse, GroupedMatches } from '@/lib/football-api'

interface UseFootballResultsProps {
  date?: string
  league?: string | number
  limit?: number
  page?: number
  autoRefresh?: boolean
  refreshInterval?: number
}

interface UseFootballResultsReturn {
  matches: FootballMatch[]
  groupedMatches: GroupedMatches
  loading: boolean
  error: string | null
  hasMore: boolean
  total: number
  currentPage: number
  perPage: number
  refresh: () => Promise<void>
  loadMore: () => Promise<void>
}

export function useFootballResults({
  date = new Date().toISOString().split('T')[0],
  league = 'all',
  limit = 30,
  page = 1,
  autoRefresh = false,
  refreshInterval = 300000 // 5 minutes (increased from 1 minute)
}: UseFootballResultsProps = {}): UseFootballResultsReturn {
  const [matches, setMatches] = useState<FootballMatch[]>([])
  const [groupedMatches, setGroupedMatches] = useState<GroupedMatches>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(page)
  const [perPage, setPerPage] = useState(limit)
  const lastFetchRef = useRef<string>('')

  const fetchMatches = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    // Always fetch all leagues, ignore league parameter for API call
    const apiLeague = 'all'
    
    // Create a unique key for this fetch
    const fetchKey = `${date}-${apiLeague}-${limit}-${pageNum}-${append}`
    
    // Prevent duplicate calls
    if (fetchKey === lastFetchRef.current && !append) {
      return
    }
    
    lastFetchRef.current = fetchKey
    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        date,
        league: apiLeague, // Use apiLeague for API call
        limit: limit.toString(),
        page: pageNum.toString()
      })

      const response = await fetch(`/api/football/finished?${params}`)
      const data: FootballApiResponse & { success: boolean; message?: string } = await response.json()

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch matches')
      }

      const newMatches = data.data || []
      
      if (append) {
        setMatches(prev => [...prev, ...newMatches])
      } else {
        setMatches(newMatches)
      }

      // Group matches by competition
      const grouped = newMatches.reduce((acc: GroupedMatches, match) => {
        const competitionId = match.competition_id?.toString() || 'unknown'
        
        if (!acc[competitionId]) {
          acc[competitionId] = {
            id: match.competition_id || 0,
            name: match.competition_name || 'Unknown Competition',
            logo: match.competition_emblem || '',
            priority: match.priority || 999,
            matches: []
          }
        }
        
        acc[competitionId].matches.push(match)
        return acc
      }, {} as GroupedMatches)

      if (append) {
        setGroupedMatches(prev => {
          const merged = { ...prev }
          Object.keys(grouped).forEach(key => {
            if (merged[key]) {
              merged[key].matches = [...merged[key].matches, ...grouped[key].matches]
            } else {
              merged[key] = grouped[key]
            }
          })
          return merged
        })
      } else {
        setGroupedMatches(grouped)
      }

      setHasMore(data.has_more)
      setTotal(data.total)
      setCurrentPage(data.page)
      setPerPage(data.per_page)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
  }, [date, limit])

  const refresh = useCallback(async () => {
    lastFetchRef.current = ''
    await fetchMatches(1, false)
  }, [fetchMatches])

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return
    await fetchMatches(currentPage + 1, true)
  }, [hasMore, loading, currentPage, fetchMatches])

  // Initial fetch - only when key parameters change
  useEffect(() => {
    // Reset the ref when parameters change
    lastFetchRef.current = ''
    fetchMatches(page, false)
  }, [date, limit, page])

  // Auto refresh - prevent unnecessary calls
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      // Reset ref to allow refresh
      lastFetchRef.current = ''
      refresh()
    }, refreshInterval)
    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval, refresh])

  return {
    matches,
    groupedMatches,
    loading,
    error,
    hasMore,
    total,
    currentPage,
    perPage,
    refresh,
    loadMore
  }
}

// Hook for upcoming matches
export function useUpcomingMatches({
  date = new Date().toISOString().split('T')[0],
  league = 'all',
  limit = 20,
  autoRefresh = true,
  refreshInterval = 180000 // 3 minutes (increased from 30 seconds)
}: Omit<UseFootballResultsProps, 'page'> = {}) {
  const [matches, setMatches] = useState<FootballMatch[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUpcomingMatches = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        date,
        league: league.toString(),
        limit: limit.toString()
      })

      const response = await fetch(`/api/football/upcoming?${params}`)
      const data: { success: boolean; data: FootballMatch[]; message?: string } = await response.json()

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch upcoming matches')
      }

      setMatches(data.data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
  }, [date, league, limit])

  // Initial fetch
  useEffect(() => {
    fetchUpcomingMatches()
  }, [fetchUpcomingMatches])

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(fetchUpcomingMatches, refreshInterval)
    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval, fetchUpcomingMatches])

  return {
    matches,
    loading,
    error,
    refresh: fetchUpcomingMatches
  }
}